<template>
  <div>
    <!-- Intro card -->
    <div class="bg-white rounded-xl border border-gray-200/60 card-shadow p-6 mb-6">
      <div class="flex items-start gap-4">
        <div class="w-11 h-11 rounded-lg bg-[var(--color-primary-light)] flex items-center justify-center shrink-0">
          <svg class="w-5 h-5 text-[var(--color-primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="16 18 22 12 16 6"/>
            <polyline points="8 6 2 12 8 18"/>
          </svg>
        </div>
        <div class="min-w-0">
          <h2 class="text-[15px] font-semibold text-gray-900">Build with {{ $appName }}</h2>
          <p class="text-sm text-gray-500 leading-relaxed mt-1">
            Reference documentation for embedding the player, subscribing to
            playback events, and integrating the SDKs into your apps. All
            links open in a new tab.
          </p>
        </div>
      </div>
    </div>

    <!-- SDKs -->
    <section class="mb-6">
      <div class="flex items-center gap-2 mb-3 px-1">
        <h3 class="text-[13px] font-semibold uppercase tracking-wider text-gray-400">SDKs</h3>
        <div class="flex-1 h-px bg-gray-100" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
        <!-- Ready SDKs render as external links; unreleased ones render
             as a static muted card with a "Coming soon" ribbon so the
             lineup stays visible without offering a dead link. -->
        <component
          v-for="sdk in sdks"
          :key="sdk.key"
          :is="sdk.comingSoon ? 'div' : 'a'"
          :href="sdk.comingSoon ? null : sdk.url"
          :target="sdk.comingSoon ? null : '_blank'"
          :rel="sdk.comingSoon ? null : 'noopener noreferrer'"
          :aria-disabled="sdk.comingSoon ? 'true' : null"
          :class="[
            'group relative bg-white rounded-xl border border-gray-200/60 card-shadow p-5 transition-all',
            sdk.comingSoon
              ? 'opacity-60 cursor-not-allowed grayscale select-none'
              : 'hover:border-[var(--color-primary)]/40 hover:shadow-md hover:shadow-gray-200/50'
          ]"
        >
          <span
            v-if="sdk.comingSoon"
            class="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold uppercase tracking-wider"
          >Coming soon</span>
          <div class="flex items-center justify-between mb-3">
            <span :class="['flex items-center justify-center w-10 h-10 rounded-lg', sdk.iconBg]" v-html="sdk.icon" />
            <svg
              v-if="!sdk.comingSoon"
              class="w-4 h-4 text-gray-300 group-hover:text-[var(--color-primary)] transition-colors"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            >
              <path d="M7 17L17 7"/>
              <polyline points="7 7 17 7 17 17"/>
            </svg>
          </div>
          <p class="text-[15px] font-semibold text-gray-900">{{ sdk.title }}</p>
          <p class="text-sm text-gray-500 mt-1 leading-relaxed">{{ sdk.description }}</p>
          <div class="flex items-center gap-1.5 mt-4 text-xs text-gray-400 font-data uppercase tracking-wider">
            <span>{{ sdk.language }}</span>
            <span v-if="sdk.badge" class="ml-auto px-2 py-0.5 rounded bg-gray-100 text-gray-500 font-medium text-[10px]">{{ sdk.badge }}</span>
          </div>
        </component>
      </div>
    </section>

    <!-- API references -->
    <section>
      <div class="flex items-center gap-2 mb-3 px-1">
        <h3 class="text-[13px] font-semibold uppercase tracking-wider text-gray-400">API References</h3>
        <div class="flex-1 h-px bg-gray-100" />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <!-- Same pattern as the SDK cards: ready docs render as external
             links; unreleased ones render as a static muted card with
             the "Coming soon" ribbon. -->
        <component
          v-for="doc in apiDocs"
          :key="doc.key"
          :is="doc.comingSoon ? 'div' : 'a'"
          :href="doc.comingSoon ? null : doc.url"
          :target="doc.comingSoon ? null : '_blank'"
          :rel="doc.comingSoon ? null : 'noopener noreferrer'"
          :aria-disabled="doc.comingSoon ? 'true' : null"
          :class="[
            'group relative bg-white rounded-xl border border-gray-200/60 card-shadow p-5 transition-all',
            doc.comingSoon
              ? 'opacity-60 cursor-not-allowed grayscale select-none'
              : 'hover:border-[var(--color-primary)]/40 hover:shadow-md hover:shadow-gray-200/50'
          ]"
        >
          <span
            v-if="doc.comingSoon"
            class="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold uppercase tracking-wider"
          >Coming soon</span>
          <div class="flex items-start gap-4">
            <span :class="['flex items-center justify-center w-10 h-10 rounded-lg shrink-0', doc.iconBg]" v-html="doc.icon" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p class="text-[15px] font-semibold text-gray-900">{{ doc.title }}</p>
                <svg
                  v-if="!doc.comingSoon"
                  class="w-4 h-4 text-gray-300 group-hover:text-[var(--color-primary)] transition-colors ml-auto"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                >
                  <path d="M7 17L17 7"/>
                  <polyline points="7 7 17 7 17 17"/>
                </svg>
              </div>
              <p class="text-sm text-gray-500 mt-1 leading-relaxed">{{ doc.description }}</p>
            </div>
          </div>
        </component>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * Developer documentation landing page. Every entry here is an
 * external link — this view has no local state, no fetches, and no
 * side effects. Just curated pointers to the SDKs and API references
 * developers need when integrating with the platform.
 *
 * The `url` values below are the CURRENT documentation locations; if
 * the docs move, update the URLs in the `sdks` / `apiDocs` computed
 * properties and nothing else needs to change.
 */
export default {
  name: 'DeveloperDocs',

  computed: {
    sdks() {
      return [
        {
          key: 'javascript',
          title: 'JavaScript SDK',
          description: 'Embed the web player, wire up event listeners, and drive playback from your site or dashboard.',
          language: 'JavaScript · TypeScript',
          badge: 'Web',
          url: '/sdk/lamusica-media-player.min.js',
          iconBg: 'bg-amber-50',
          icon: '<svg class="w-5 h-5 text-amber-500" viewBox="0 0 24 24" fill="currentColor"><path d="M0 0h24v24H0V0zm22.034 18.276c-.175-1.095-.888-2.015-3.003-2.873-.736-.345-1.554-.585-1.797-1.14-.091-.33-.105-.51-.046-.705.15-.646.915-.84 1.515-.66.39.12.75.42.976.9 1.034-.676 1.034-.676 1.755-1.125-.27-.42-.404-.601-.586-.78-.63-.705-1.469-1.065-2.834-1.034l-.705.089c-.676.165-1.32.525-1.71 1.005-1.14 1.291-.811 3.541.569 4.471 1.365 1.02 3.361 1.244 3.616 2.205.24 1.17-.87 1.545-1.966 1.41-.811-.18-1.26-.586-1.755-1.336l-1.83 1.051c.21.48.45.689.81 1.109 1.74 1.756 6.09 1.666 6.871-1.004.029-.09.24-.705.074-1.65l.046.067zm-8.983-7.245h-2.248c0 1.938-.009 3.864-.009 5.805 0 1.232.063 2.363-.138 2.711-.33.689-1.18.601-1.566.48-.396-.196-.597-.466-.83-.855-.063-.105-.11-.196-.127-.196l-1.825 1.125c.305.63.75 1.172 1.324 1.517.855.51 2.004.675 3.207.405.783-.226 1.458-.691 1.811-1.411.51-.93.402-2.07.397-3.346.012-2.054 0-4.109 0-6.179l.004-.056z"/></svg>'
        },
        {
          key: 'ios',
          title: 'iOS SDK',
          description: 'Native Swift SDK for iPhone and iPad. AVFoundation-backed playback with full event instrumentation.',
          language: 'Swift · Objective-C',
          badge: 'iOS 14+',
          url: '#',
          iconBg: 'bg-gray-100',
          icon: '<svg class="w-5 h-5 text-gray-700" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>',
          comingSoon: true
        },
        {
          key: 'android',
          title: 'Android SDK',
          description: 'Native Kotlin SDK for Android devices, TVs and Auto. Media3 / ExoPlayer under the hood.',
          language: 'Kotlin · Java',
          badge: 'API 21+',
          url: '#',
          iconBg: 'bg-green-50',
          icon: '<svg class="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.523 15.34a1.045 1.045 0 110-2.088 1.045 1.045 0 010 2.088m-11.046 0a1.045 1.045 0 110-2.088 1.045 1.045 0 010 2.088m11.42-6.056l2.086-3.615a.434.434 0 00-.752-.434l-2.113 3.66a13.056 13.056 0 00-5.318-1.126c-1.914 0-3.719.408-5.318 1.126L4.87 5.235a.434.434 0 00-.752.434l2.086 3.615C2.635 11.148.256 14.716 0 18.917h24c-.256-4.201-2.635-7.769-6.104-9.633"/></svg>',
          comingSoon: true
        }
      ]
    },

    apiDocs() {
      return [
        {
          key: 'player-events',
          title: 'Player Events API',
          description: 'Every event the player emits (session, playback, ads, buffering, errors) with payload shape and firing conditions. Use this when subscribing from any SDK.',
          url: this.$playerEventsUrl,
          iconBg: 'bg-violet-50',
          icon: '<svg class="w-5 h-5 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>'
        },
        {
          key: 'rest-api',
          title: 'REST API Reference',
          description: 'Manage livestreams, recordings, medias, exports and analytics from your own tooling. Auth, endpoints, request / response schemas.',
          url: '#',
          iconBg: 'bg-blue-50',
          icon: '<svg class="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17l6-6-6-6"/><line x1="12" y1="19" x2="20" y2="19"/></svg>',
          comingSoon: true
        }
      ]
    }
  }
}
</script>
